import type { PaymentIntent, PaymentIntentStatus } from '../types';
import * as paymentApi from '../api/payment';
import { KeyedAsyncCache } from './asyncCache';
import { refreshLoansForFarmer } from './loans';

const STATUS_FROM_BACKEND: Record<paymentApi.BackendPaymentIntentStatus, PaymentIntentStatus> = {
  PENDING: 'qr_created',
  FARMER_CONFIRMED: 'farmer_confirmed_transfer',
  PAID: 'bank_credit_received',
  WAITING_HTX_CONFIRMATION: 'waiting_htx_confirmation',
  SETTLED: 'settled',
  RECONCILE_FAILED: 'reconcile_failed',
  EXPIRED: 'cancelled',
  FAILED: 'reconcile_failed',
  CANCELLED: 'cancelled',
  COMPLAINT: 'complaint',
};

function toFrontendIntent(i: paymentApi.BackendPaymentIntent): PaymentIntent {
  return {
    id: i._id,
    debtId: i.loanId || i.subjectRef,
    farmerId: i.farmerId || '',
    loanId: i.loanId,
    soTien: Number(i.amountVnd),
    goc: i.principalVnd !== undefined ? Number(i.principalVnd) : undefined,
    laiTichLuy: i.interestVnd !== undefined ? Number(i.interestVnd) : undefined,
    laiSuatApDung: i.interestRatePercentPerYear,
    // The real transfer-content memo the bank actually matches on — not a
    // client-invented string, unlike the old mock's "AGRIDA PAY {maKhoanVay}".
    noiDungChuyenKhoan: i.vietQrReconciliationCode,
    nguoiTra: '',
    status: STATUS_FROM_BACKEND[i.status],
    qrCreatedAt: '',
    farmerConfirmedAt: i.farmerConfirmedAt,
    bankCreditMatch: i.status === 'SETTLED',
    htxConfirmedAt: i.htxConfirmedAt,
    settledAt: i.settledAt,
    cancelledAt: i.cancelledAt,
    cancelReason: i.cancelReason,
    complaintAt: i.complaintAt,
    complaintReason: i.complaintReason,
    reconcileError: i.reconcileError,
  };
}

const byFarmer = new KeyedAsyncCache<PaymentIntent[]>(
  async (farmerId) => (await paymentApi.listPaymentIntentsByFarmer(farmerId)).map(toFrontendIntent),
  [],
  'payments:byFarmer',
);

export function getPaymentIntentsByFarmer(farmerId?: string): PaymentIntent[] {
  if (!farmerId) return [];
  return byFarmer.get(farmerId);
}

export function getActivePaymentIntentByLoan(farmerId: string, loanId: string): PaymentIntent | undefined {
  return getPaymentIntentsByFarmer(farmerId).find(
    (p) => p.loanId === loanId && p.status !== 'settled' && p.status !== 'cancelled',
  );
}

function refresh(farmerId: string) {
  byFarmer.invalidate(farmerId);
  byFarmer.get(farmerId);
}

export async function createRepaymentIntent(data: { farmerId: string; loanId: string; amountVnd: number }): Promise<PaymentIntent> {
  const created = await paymentApi.createPaymentIntent({
    subjectRef: `LOAN-${data.loanId}`,
    amountVnd: data.amountVnd,
    farmerId: data.farmerId,
    loanId: data.loanId,
  });
  refresh(data.farmerId);
  return toFrontendIntent(created);
}

export async function confirmTransfer(intentId: string, farmerId: string): Promise<PaymentIntent> {
  const updated = await paymentApi.confirmTransfer(intentId, farmerId);
  refresh(farmerId);
  return toFrontendIntent(updated);
}

export async function htxConfirmReceipt(intentId: string, farmerId: string): Promise<PaymentIntent> {
  const updated = await paymentApi.htxManualConfirm(intentId);
  refresh(farmerId);
  // Settlement flips the linked loan ACTIVE→SETTLED server-side (payment_intent.settled
  // listener in loan.service.ts) — refresh so the farmer's loan/debt view catches up.
  refreshLoansForFarmer(farmerId);
  return toFrontendIntent(updated);
}

export async function complaintPaymentIntent(intentId: string, farmerId: string, reason: string): Promise<PaymentIntent> {
  const updated = await paymentApi.filePaymentComplaint(intentId, reason);
  refresh(farmerId);
  return toFrontendIntent(updated);
}

export async function cancelRepaymentIntent(intentId: string, farmerId: string, reason?: string): Promise<PaymentIntent> {
  const updated = await paymentApi.cancelPaymentIntent(intentId, reason);
  refresh(farmerId);
  return toFrontendIntent(updated);
}
